/**
 * Graph options: the single source of truth for API query parameters.
 *
 * - `parseGraphOptions` validates untrusted input (query string) and falls back to safe defaults.
 * - `serializeGraphOptions` does the reverse, emitting only non-default values that matter for
 *   the selected graph type, so the same URL reproduces the same graph without stored state.
 *
 * To add an option: add it to `GraphOptions`, `BASE_DEFAULTS` (or `TYPE_DEFAULTS`), one of the
 * field tables (or `ENUM_FIELDS`), and `FIELD_SCOPE` if it only applies to some graph types.
 */
import { addDays, daysBetween, isIsoDate, toIsoDate } from './shared/dates';
import { GraphColors, THEME_NAMES, ThemeName, themeColors } from './themes';

export const GRAPH_TYPES = ['heatmap', 'line', 'combo'] as const;
export type GraphType = (typeof GRAPH_TYPES)[number];

export const RANGES = ['7d', '30d', '90d', '6m', '1y', 'custom'] as const;
export type RangeName = (typeof RANGES)[number];

export const GRID_STYLES = ['dotted', 'dashed', 'solid'] as const;
export type GridStyle = (typeof GRID_STYLES)[number];

/** Which series the combo graph draws. */
export const SERIES_MODES = ['both', 'bars', 'line'] as const;
export type SeriesMode = (typeof SERIES_MODES)[number];

export const GROUP_BY = ['auto', 'day', 'week', 'month'] as const;
export type GroupBy = (typeof GROUP_BY)[number];

/** Values derived per bucket from daily counts (see shared/aggregate.ts). */
export const METRIC_NAMES = [
    'auto',
    'contributions',
    'activeDays',
    'average',
    'best',
    'trend',
    'cumulative',
] as const;
export type MetricName = (typeof METRIC_NAMES)[number];

export const LEGEND_POSITIONS = ['top', 'bottom'] as const;
export type LegendPosition = (typeof LEGEND_POSITIONS)[number];

export const FONT_STACKS = {
    system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif",
    sans: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    serif: "Georgia, 'Times New Roman', Times, serif",
    mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
} as const;

export type FontName = keyof typeof FONT_STACKS;
export const FONT_NAMES = Object.keys(FONT_STACKS) as FontName[];

/** Earliest date GitHub has contribution data for. */
export const MIN_DATE = '2008-01-01';
/** Custom ranges are limited to one year (GitHub's API limit). */
export const MAX_RANGE_DAYS = 365;

export interface GraphOptions {
    username: string;
    type: GraphType;
    theme: ThemeName;
    colors: GraphColors;
    range: RangeName;
    /** Only set when `range` is `custom` (YYYY-MM-DD, inclusive). */
    from: string;
    to: string;

    // Shared layout
    /** Output size in px; 0 = automatic. */
    width: number;
    height: number;
    padding: number;
    /** Corner radius of the card. */
    borderRadius: number;
    borderWidth: number;
    fontSize: number;
    font: FontName;
    /** Custom title; empty = automatic. */
    title: string;
    showTitle: boolean;
    showTotal: boolean;
    showBorder: boolean;
    showTooltips: boolean;

    // Heatmap
    cellSize: number;
    cellGap: number;
    /** Corner radius of each day cell. */
    radius: number;
    showMonths: boolean;
    showWeekdays: boolean;
    showLegend: boolean;

    // Line graph
    lineWidth: number;
    /** 0 = straight segments, 100 = fully smoothed curve. */
    smoothing: number;
    pointRadius: number;
    pointBorderWidth: number;
    /** Percent, 0-100. */
    areaOpacity: number;
    /** Percent, 0-100. */
    gridOpacity: number;
    gridStyle: GridStyle;
    showPoints: boolean;
    showArea: boolean;
    /** Horizontal grid lines (at Y ticks). */
    showGridY: boolean;
    /** Vertical grid lines (at X labels). */
    showGridX: boolean;
    showXLabels: boolean;
    showYLabels: boolean;
    showAxisTitles: boolean;
    showValues: boolean;
    /** Percent, 0-100. */
    lineOpacity: number;
    /** Fade the line in from left to right. */
    lineGradient: boolean;

    // Combo (bar + line)
    series: SeriesMode;
    groupBy: GroupBy;
    barMetric: MetricName;
    lineMetric: MetricName;
    /** Bar width as a percentage of its slot; the rest is spacing. */
    barWidth: number;
    barRadius: number;
    /** Percent, 0-100. */
    barOpacity: number;
    showLeftAxis: boolean;
    showRightAxis: boolean;
    legendPosition: LegendPosition;
}

type NumberKey = {
    [K in keyof GraphOptions]: GraphOptions[K] extends number ? K : never;
}[keyof GraphOptions];
type BooleanKey = {
    [K in keyof GraphOptions]: GraphOptions[K] extends boolean ? K : never;
}[keyof GraphOptions];

export interface NumberField {
    min: number;
    max: number;
    /** When true, 0 is also accepted and means "auto". */
    allowAuto?: boolean;
}

export const NUMBER_FIELDS: Record<NumberKey, NumberField> = {
    width: { min: 200, max: 2000, allowAuto: true },
    height: { min: 80, max: 1000, allowAuto: true },
    padding: { min: 0, max: 64 },
    borderRadius: { min: 0, max: 32 },
    borderWidth: { min: 0, max: 8 },
    fontSize: { min: 8, max: 24 },
    cellSize: { min: 4, max: 30 },
    cellGap: { min: 0, max: 10 },
    radius: { min: 0, max: 15 },
    lineWidth: { min: 1, max: 10 },
    smoothing: { min: 0, max: 100 },
    pointRadius: { min: 0, max: 12 },
    pointBorderWidth: { min: 0, max: 6 },
    areaOpacity: { min: 0, max: 100 },
    gridOpacity: { min: 0, max: 100 },
    lineOpacity: { min: 0, max: 100 },
    barWidth: { min: 10, max: 100 },
    barRadius: { min: 0, max: 20 },
    barOpacity: { min: 0, max: 100 },
};

export const BOOLEAN_FIELDS: BooleanKey[] = [
    'showTitle',
    'showTotal',
    'showBorder',
    'showTooltips',
    'showMonths',
    'showWeekdays',
    'showLegend',
    'showPoints',
    'showArea',
    'showGridY',
    'showGridX',
    'showXLabels',
    'showYLabels',
    'showAxisTitles',
    'showValues',
    'lineGradient',
    'showLeftAxis',
    'showRightAxis',
];

type EnumKey =
    | 'font'
    | 'gridStyle'
    | 'series'
    | 'groupBy'
    | 'barMetric'
    | 'lineMetric'
    | 'legendPosition';

/** Options with a fixed set of values (`range` and `type` are handled separately). */
export const ENUM_FIELDS: Record<EnumKey, readonly string[]> = {
    font: Object.keys(FONT_STACKS),
    gridStyle: GRID_STYLES,
    series: SERIES_MODES,
    groupBy: GROUP_BY,
    barMetric: METRIC_NAMES,
    lineMetric: METRIC_NAMES,
    legendPosition: LEGEND_POSITIONS,
};

/** Query parameter -> color key. */
export const COLOR_PARAMS: Record<string, keyof GraphColors> = {
    background: 'background',
    text: 'text',
    border: 'border',
    labelColor: 'label',
    countColor: 'count',
    empty: 'empty',
    color1: 'color1',
    color2: 'color2',
    color3: 'color3',
    color4: 'color4',
    lineColor: 'line',
    pointColor: 'point',
    pointBorderColor: 'pointBorder',
    areaColor: 'area',
    gridColor: 'grid',
    barColor: 'bar',
};

/**
 * Options that only affect some graph types. Anything not listed is shared.
 * Used to keep generated URLs short and to show the right controls in the UI.
 */
const HEATMAP: GraphType[] = ['heatmap'];
const LINE: GraphType[] = ['line'];
const CHARTS: GraphType[] = ['line', 'combo'];
const COMBO: GraphType[] = ['combo'];

/**
 * Options that only affect some graph types. Anything not listed is shared.
 * Used to keep generated URLs short and to show the right controls in the UI.
 */
export const FIELD_SCOPE: Partial<Record<keyof GraphOptions | keyof GraphColors, GraphType[]>> = {
    cellSize: HEATMAP,
    cellGap: HEATMAP,
    radius: HEATMAP,
    showMonths: HEATMAP,
    showWeekdays: HEATMAP,
    empty: HEATMAP,
    color1: HEATMAP,
    color2: HEATMAP,
    color3: HEATMAP,
    color4: HEATMAP,
    showLegend: ['heatmap', 'combo'],
    areaOpacity: LINE,
    showArea: LINE,
    showYLabels: LINE,
    showAxisTitles: LINE,
    showValues: LINE,
    area: LINE,
    lineWidth: CHARTS,
    smoothing: CHARTS,
    pointRadius: CHARTS,
    pointBorderWidth: CHARTS,
    gridOpacity: CHARTS,
    gridStyle: CHARTS,
    showPoints: CHARTS,
    showGridY: CHARTS,
    showGridX: CHARTS,
    showXLabels: CHARTS,
    lineOpacity: CHARTS,
    lineGradient: CHARTS,
    line: CHARTS,
    point: CHARTS,
    pointBorder: CHARTS,
    grid: CHARTS,
    series: COMBO,
    groupBy: CHARTS,
    barMetric: COMBO,
    lineMetric: COMBO,
    barWidth: COMBO,
    barRadius: COMBO,
    barOpacity: COMBO,
    showLeftAxis: COMBO,
    showRightAxis: COMBO,
    legendPosition: COMBO,
    bar: COMBO,
};

export const appliesTo = (key: string, type: GraphType): boolean => {
    const scope = FIELD_SCOPE[key as keyof typeof FIELD_SCOPE];
    return !scope || scope.includes(type);
};

/** Accepted alternative spellings (including the legacy `/graph` names). */
const PARAM_ALIASES: Record<string, string> = {
    bg: 'background',
    bg_color: 'background',
    bgColor: 'background',
    text_color: 'text',
    textColor: 'text',
    border_color: 'border',
    borderColor: 'border',
    label_color: 'labelColor',
    count_color: 'countColor',
    line: 'lineColor',
    line_color: 'lineColor',
    point: 'pointColor',
    point_color: 'pointColor',
    area_color: 'areaColor',
    grid_color: 'gridColor',
    bar_color: 'barColor',
    bar_width: 'barWidth',
    bar_radius: 'barRadius',
    cell_size: 'cellSize',
    cell_gap: 'cellGap',
    border_radius: 'borderRadius',
    font_size: 'fontSize',
    line_width: 'lineWidth',
    point_radius: 'pointRadius',
    graph: 'type',
};

const TYPE_ALIASES: Record<string, GraphType> = {
    heatmap: 'heatmap',
    calendar: 'heatmap',
    line: 'line',
    area: 'line',
    combo: 'combo',
    barline: 'combo',
    'bar-line': 'combo',
};

export const TITLE_MAX_LENGTH = 60;

/** Per-type defaults that differ from the shared base. */
export const TYPE_DEFAULTS: Record<GraphType, { theme: ThemeName } & Partial<GraphOptions>> = {
    heatmap: { theme: 'light', range: '1y' },
    // Daily points by default so existing line-graph URLs render exactly as before.
    line: { theme: 'lime', range: '30d', padding: 24, groupBy: 'day' },
    combo: {
        theme: 'graphite',
        range: '1y',
        padding: 24,
        showPoints: false,
        gridStyle: 'solid',
        gridOpacity: 25,
        showGridX: false,
    },
};

export const DEFAULT_TYPE: GraphType = 'heatmap';
export const DEFAULT_THEME: ThemeName = TYPE_DEFAULTS[DEFAULT_TYPE].theme;

const BASE_DEFAULTS: Omit<GraphOptions, 'type' | 'theme' | 'colors'> = {
    username: '',
    range: '1y',
    from: '',
    to: '',
    width: 0,
    height: 0,
    padding: 16,
    borderRadius: 6,
    borderWidth: 1,
    fontSize: 12,
    font: 'system',
    title: '',
    showTitle: true,
    showTotal: true,
    showBorder: true,
    showTooltips: true,
    cellSize: 11,
    cellGap: 3,
    radius: 2,
    showMonths: true,
    showWeekdays: true,
    showLegend: true,
    lineWidth: 3,
    smoothing: 60,
    pointRadius: 4,
    pointBorderWidth: 2,
    areaOpacity: 30,
    gridOpacity: 50,
    gridStyle: 'dotted',
    showPoints: true,
    showArea: true,
    showGridY: true,
    showGridX: true,
    showXLabels: true,
    showYLabels: true,
    showAxisTitles: true,
    showValues: false,
    lineOpacity: 100,
    lineGradient: false,
    series: 'both',
    groupBy: 'auto',
    barMetric: 'contributions',
    lineMetric: 'auto',
    barWidth: 55,
    barRadius: 4,
    barOpacity: 100,
    showLeftAxis: true,
    showRightAxis: true,
    legendPosition: 'top',
};

export const defaultOptions = (type: GraphType = DEFAULT_TYPE, theme?: ThemeName): GraphOptions => {
    const typeDefaults = TYPE_DEFAULTS[type];
    const resolvedTheme = theme ?? typeDefaults.theme;
    return {
        ...BASE_DEFAULTS,
        ...typeDefaults,
        type,
        theme: resolvedTheme,
        colors: themeColors(resolvedTheme),
    };
};

export type RawQuery = Record<string, unknown>;

const firstString = (value: unknown): string | undefined => {
    if (Array.isArray(value)) return firstString(value[0]);
    return typeof value === 'string' ? value : undefined;
};

/** Returns `rrggbb`/`rrggbbaa`/`transparent` or undefined when invalid. */
export const normalizeColor = (value: unknown): string | undefined => {
    const raw = firstString(value)?.trim().toLowerCase().replace(/^#/, '');
    if (!raw) return undefined;
    if (raw === 'transparent') return raw;
    if (!/^([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/.test(raw)) return undefined;
    // Expand shorthand (abc -> aabbcc) so values are canonical.
    return raw.length <= 4 ? [...raw].map((c) => c + c).join('') : raw;
};

export const parseBoolean = (value: unknown): boolean | undefined => {
    const raw = firstString(value)?.trim().toLowerCase();
    if (raw === undefined) return undefined;
    if (['true', '1', 'yes', 'on'].includes(raw)) return true;
    if (['false', '0', 'no', 'off'].includes(raw)) return false;
    return undefined;
};

export const parseNumber = (value: unknown, field: NumberField): number | undefined => {
    const raw = firstString(value)?.trim();
    if (!raw) return undefined;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return undefined;
    const rounded = Math.round(parsed);
    if (field.allowAuto && rounded <= 0) return 0;
    return Math.min(Math.max(rounded, field.min), field.max);
};

const parseEnum = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined => {
    const raw = firstString(value)?.trim().toLowerCase();
    return allowed.find((item) => item.toLowerCase() === raw);
};

/** Strips control characters and trims to a safe length. Escaping happens at render time. */
const sanitizeTitle = (value: unknown): string =>
    (firstString(value) ?? '')
        .replace(/[\u0000-\u001f\u007f]/g, '')
        .trim()
        .slice(0, TITLE_MAX_LENGTH);

/**
 * Validates a custom date window: both dates must be real, not in the future, not before
 * GitHub existed, and at most a year apart. Returns undefined when unusable.
 */
export const normalizeWindow = (
    fromValue: unknown,
    toValue: unknown,
    today: string = toIsoDate(Date.now()),
): { from: string; to: string } | undefined => {
    let from = firstString(fromValue)?.trim();
    let to = firstString(toValue)?.trim();
    if (!isIsoDate(from) || !isIsoDate(to)) return undefined;
    if (from > to) [from, to] = [to, from];
    if (to > today) to = today;
    if (from > to) from = to;
    if (to < MIN_DATE) return undefined;
    if (from < MIN_DATE) from = MIN_DATE;
    if (daysBetween(from, to) >= MAX_RANGE_DAYS) from = addDays(to, -(MAX_RANGE_DAYS - 1));
    return { from, to };
};

const resolveAliases = (query: RawQuery): RawQuery => {
    const resolved: RawQuery = {};
    for (const [key, value] of Object.entries(query)) {
        const canonical = PARAM_ALIASES[key] ?? key;
        // Canonical names win over aliases.
        if (!(canonical in resolved) || canonical === key) resolved[canonical] = value;
    }
    return resolved;
};

/**
 * Validates an untrusted query object. Never throws: invalid values fall back to defaults.
 * The username is returned as given (trimmed); validate it separately with `parseUsername`.
 */
export const parseGraphOptions = (input: RawQuery, today?: string): GraphOptions => {
    const query = resolveAliases(input);
    const typeParam = firstString(query.type)?.trim().toLowerCase() ?? '';
    const type = TYPE_ALIASES[typeParam] ?? DEFAULT_TYPE;
    const options = defaultOptions(type, parseEnum(query.theme, THEME_NAMES));

    options.username = (firstString(query.username) ?? '').trim();

    for (const [param, colorKey] of Object.entries(COLOR_PARAMS)) {
        const color = normalizeColor(query[param]);
        if (color) options.colors[colorKey] = color;
    }

    for (const [key, field] of Object.entries(NUMBER_FIELDS) as Array<[NumberKey, NumberField]>) {
        const parsed = parseNumber(query[key], field);
        if (parsed !== undefined) options[key] = parsed;
    }

    for (const key of BOOLEAN_FIELDS) {
        const parsed = parseBoolean(query[key]);
        if (parsed !== undefined) options[key] = parsed;
    }

    for (const [key, allowed] of Object.entries(ENUM_FIELDS) as Array<
        [EnumKey, readonly string[]]
    >) {
        const value = parseEnum(query[key], allowed);
        if (value !== undefined) (options[key] as string) = value;
    }
    options.title = sanitizeTitle(query.title);

    const range = parseEnum(query.range, RANGES);
    if (range === 'custom') {
        const window = normalizeWindow(query.from, query.to, today);
        if (window) Object.assign(options, { range, ...window });
    } else if (range) {
        options.range = range;
    }

    return options;
};

/**
 * Serializes options into query parameters, omitting defaults and settings that do not apply
 * to the selected graph type. For the `custom` theme every relevant color is written out so
 * the URL stays self-contained.
 */
export const serializeGraphOptions = (options: GraphOptions): URLSearchParams => {
    const params = new URLSearchParams();
    const { type } = options;
    const defaults = defaultOptions(type, options.theme);

    if (options.username) params.set('username', options.username);
    if (type !== DEFAULT_TYPE) params.set('type', type);
    if (options.theme !== TYPE_DEFAULTS[type].theme) params.set('theme', options.theme);

    if (options.range !== defaults.range) params.set('range', options.range);
    if (options.range === 'custom') {
        params.set('from', options.from);
        params.set('to', options.to);
    }

    for (const [param, colorKey] of Object.entries(COLOR_PARAMS)) {
        if (!appliesTo(colorKey, type)) continue;
        const value = options.colors[colorKey];
        if (options.theme === 'custom' || value !== defaults.colors[colorKey])
            params.set(param, value);
    }

    for (const key of [...(Object.keys(NUMBER_FIELDS) as NumberKey[]), ...BOOLEAN_FIELDS]) {
        if (appliesTo(key, type) && options[key] !== defaults[key])
            params.set(key, String(options[key]));
    }

    for (const key of Object.keys(ENUM_FIELDS) as EnumKey[]) {
        if (appliesTo(key, type) && options[key] !== defaults[key]) params.set(key, options[key]);
    }
    if (options.title) params.set('title', options.title);

    return params;
};
